import { useMemo } from 'react';
import { useOrgaConfigurationContext } from '@/app/providers';
import { isSendcloudPlatformProviderEnabled } from '@/shared/config/featureFlags';

export interface ShippingMethod {
  id: string;
  name: string;
  description: string;
  price: number;
  estimatedHours: number;
  isEnabled: boolean;
  provider: 'sendcloud' | 'sendcloud_api_key' | 'mondial_relay';
  carrier: string;
  sendcloudMethodId?: number;
  availableCountries: string[];
  minWeight?: number;
  maxWeight?: number;
  type: 'relay' | 'locker' | 'home';
  isHomeDelivery: boolean;
  getPriceForCountry: (countryCode: string) => number;
}

interface UseShippingMethodsReturn {
  availableShippingMethods: ShippingMethod[];
  getShippingMethodsForCountry: (countryCode: string, weightKg?: number) => ShippingMethod[];
  getShippingMethodById: (methodId: string) => ShippingMethod | null;
  updateMethodPriceForCountry: (methodId: string, countryCode: string) => number;
  loading: boolean;
  error: string | null;
}

export const useShippingMethods = (): UseShippingMethodsReturn => {
  const { configuration, loading: configLoading } = useOrgaConfigurationContext();
  const orgId = configuration?.organizationId;
  const canUseSendcloudPlatform = isSendcloudPlatformProviderEnabled(orgId);

  const availableShippingMethods = useMemo((): ShippingMethod[] => {
    // New multi-provider format
    const newMethods = configuration?.shippingConfig?.methods;
    if (newMethods?.length) {
      return newMethods
        .filter(m => m.isActive)
        .filter(m => m.provider !== 'sendcloud' || canUseSendcloudPlatform)
        .map(m => {
          const getPriceForCountry = (countryCode: string): number =>
            m.countryPrices?.[countryCode] ?? Object.values(m.countryPrices ?? {})[0] ?? 0;

          const carrierDisplay =
            m.provider === 'mondial_relay'
              ? 'Mondial Relay'
              : m.provider === 'sendcloud_api_key'
                ? `${m.carrier} (Sendcloud compte propre)`
                : m.carrier;

        return {
            id: m.id,
            name:
              m.customName ||
              `${carrierDisplay} – ${m.type === 'home' ? 'Domicile' : m.type === 'relay' ? 'Point relais' : 'Consigne'}`,
            description: `Livraison via ${carrierDisplay}`,
            price: Object.values(m.countryPrices ?? {})[0] ?? 0,
            estimatedHours: 72,
          isEnabled: true,
            provider: m.provider,
            carrier: m.carrier,
            sendcloudMethodId: m.sendcloudMethodId,
            availableCountries: m.availableCountries ?? [],
            minWeight: m.weightRange?.min,
            maxWeight: m.weightRange?.max,
            type: m.type,
            isHomeDelivery: m.type === 'home',
            getPriceForCountry,
          };
        });
    }

    return [];
  }, [configuration, canUseSendcloudPlatform]);

  const getShippingMethodsForCountry = (countryCode: string, weightKg?: number): ShippingMethod[] => {
    return availableShippingMethods.filter(method => {
      const countryOk =
        method.availableCountries.length === 0 || method.availableCountries.includes(countryCode);
      const weightOk =
        weightKg === undefined ||
        ((method.minWeight === undefined || weightKg >= method.minWeight) &&
          (method.maxWeight === undefined || weightKg <= method.maxWeight));
      return countryOk && weightOk;
    });
  };

  const getShippingMethodById = (methodId: string): ShippingMethod | null =>
    availableShippingMethods.find(m => m.id === methodId) ?? null;

  const updateMethodPriceForCountry = (methodId: string, countryCode: string): number => {
    const method = getShippingMethodById(methodId);
    return method?.getPriceForCountry(countryCode) ?? 0;
  };

  const hasAnyMethod = (configuration?.shippingConfig?.methods?.length ?? 0) > 0;

  const error = !configLoading && !hasAnyMethod
    ? 'Aucune méthode de livraison configurée'
    : null;

  return {
    availableShippingMethods,
    getShippingMethodsForCountry,
    getShippingMethodById,
    updateMethodPriceForCountry,
    loading: configLoading,
    error,
  };
};
